import { useTranslation } from 'react-i18next';
import { Card } from '../ui/Card';
import type { CompetitorRental, SecondaryMarketData } from '../../types/api.js';

interface CompetitorCardProps {
  competitorRentals: CompetitorRental[];
  secondaryMarket: SecondaryMarketData[];
  concerns: string[];
}

/** @graph-connects none */
const LIQUIDITY_LABELS: Record<string, { label: string; color: string }> = {
  high: { label: 'market.liquidityHigh', color: 'var(--color-success)' },
  medium: { label: 'market.liquidityMedium', color: 'var(--color-warning)' },
  low: { label: 'market.liquidityLow', color: 'var(--color-text-muted)' },
};

/**
 * @graph-stack mall
 * @graph-domain Operations
 * @graph-business 競合・二次流通・口コミ懸念表示。他社レンタル月額比較、メルカリ/aucfan相場、ネガティブ口コミを集約表示
 * @graph-connects none
 */
export function CompetitorCard({
  competitorRentals,
  secondaryMarket,
  concerns,
}: CompetitorCardProps) {
  const { t } = useTranslation();

  const hasRentals = competitorRentals.length > 0;
  const hasSecondary = secondaryMarket.length > 0;
  const hasConcerns = concerns.length > 0;

  if (!hasRentals && !hasSecondary && !hasConcerns) return null;

  return (
    <Card variant="glass">
      <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-3">
        {t('market.competitorTitle')}
      </h3>

      <div className="space-y-4">
        {/* 他社レンタル */}
        {hasRentals && (
          <div>
            <h4 className="text-xs font-semibold text-[var(--color-text-secondary)] mb-2">
              {t('market.rentalComparison')}
            </h4>
            <div className="space-y-1.5">
              {competitorRentals.map((r) => (
                <div key={r.site} className="flex items-center justify-between text-sm">
                  <span className="text-[var(--color-text-secondary)]">{r.site}</span>
                  <div className="flex items-center gap-2">
                    {r.monthly != null ? (
                      <span className="font-mono font-medium text-[var(--color-text-primary)]">
                        {t('market.yen')}
                        {r.monthly.toLocaleString()}/{t('market.month')}
                      </span>
                    ) : (
                      <span className="text-xs text-[var(--color-text-muted)]">
                        {r.hasListing ? t('market.listingFound') : t('market.noListing')}
                      </span>
                    )}
                    {r.rating != null && (
                      <span className="text-xs text-[var(--color-text-muted)]">
                        {'★'}
                        {r.rating.toFixed(1)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 二次流通 */}
        {hasSecondary && (
          <div>
            <h4 className="text-xs font-semibold text-[var(--color-text-secondary)] mb-2">
              {t('market.secondaryMarket')}
            </h4>
            <div className="space-y-1.5">
              {secondaryMarket.map((s) => {
                const liq = LIQUIDITY_LABELS[s.liquidity] ?? LIQUIDITY_LABELS.low;
                return (
                  <div key={s.site} className="flex items-center justify-between text-sm">
                    <span className="text-[var(--color-text-secondary)]">{s.site}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[var(--color-text-primary)]">
                        {t('market.yen')}
                        {s.avg.toLocaleString()}
                      </span>
                      <span className="text-xs text-[var(--color-text-muted)]">
                        ({t('market.yen')}
                        {s.low.toLocaleString()}〜{t('market.yen')}
                        {s.high.toLocaleString()})
                      </span>
                      <span className="text-xs font-medium" style={{ color: liq.color }}>
                        {t(liq.label)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 口コミ懸念 */}
        {hasConcerns && (
          <div>
            <h4 className="text-xs font-semibold text-[var(--color-text-secondary)] mb-2">
              {t('market.concerns')}
            </h4>
            <div className="space-y-1.5">
              {concerns.map((c, i) => (
                <div key={i} className="risk-warning">
                  <span className="mr-2">⚠</span>
                  <span className="text-sm text-[var(--color-text-primary)]">{c}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
